/**
 * @file history-seen.ts
 * @module app/modules/service-request/utils/history-seen
 * @description Remembers, in this browser only, when each user last opened their service request
 * History tab. Decisions after that time count as new. Nothing is stored on the server.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

const STORAGE_PREFIX = 'serviceRequest.lastSeenHistory.';

const storageKeyFor = (userId: string) => `${STORAGE_PREFIX}${userId}`;

/** True when a storage event is about some user's last-seen time (another tab opened History). */
export const isHistorySeenKey = (key: string | null) =>
  Boolean(key?.startsWith(STORAGE_PREFIX));

/**
 * When this user last opened History. The first time (or if storage is blocked), it starts
 * from now, so old decisions are never counted as new.
 */
export const getHistorySeenAt = (userId: string): string => {
  const now = new Date().toISOString();
  try {
    const saved = localStorage.getItem(storageKeyFor(userId));
    if (saved && !Number.isNaN(new Date(saved).getTime())) return saved;
    localStorage.setItem(storageKeyFor(userId), now);
  } catch {
    // Storage unavailable (private mode, blocked): treat everything as seen.
  }
  return now;
};

/** Marks History as seen now; returns the previous time so the list can mark newer rows. */
export const markHistorySeen = (userId: string): string => {
  const previous = getHistorySeenAt(userId);
  try {
    localStorage.setItem(storageKeyFor(userId), new Date().toISOString());
  } catch {
    // Storage unavailable: the badge falls back to counting from now next time.
  }
  return previous;
};
